import type { MilestoneId, OnboardingProgress, FeatureUnlock, Milestone } from '@/types/onboarding';
import { MILESTONES } from '@/types/onboarding';

const STORAGE_KEY = 'deeply_smart_onboarding';
const USER_META_KEY = 'deeply_smart_user_meta';
const TEST_EMAIL = 'newuser@gmail.com';
// Cutoff date: only accounts created AFTER this date go through onboarding
// Existing/mature accounts are auto-unlocked
const ONBOARDING_LAUNCH_DATE = new Date('2026-01-01T00:00:00Z');

interface UserMeta {
  userId: string;
  email?: string;
  createdAt?: string;
  isTestAccount: boolean;
}

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY}_${userId}`;
}

function getUserMetaKey(userId: string): string {
  return `${USER_META_KEY}_${userId}`;
}

function saveUserMeta(userId: string, email?: string, createdAt?: Date): UserMeta {
  const meta: UserMeta = {
    userId,
    email,
    createdAt: createdAt?.toISOString(),
    isTestAccount: email?.toLowerCase() === TEST_EMAIL,
  };
  localStorage.setItem(getUserMetaKey(userId), JSON.stringify(meta));
  return meta;
}

function getUserMeta(userId: string): UserMeta | null {
  const stored = localStorage.getItem(getUserMetaKey(userId));
  if (stored) {
    return JSON.parse(stored);
  }
  return null;
}

function isExistingUserByMeta(meta: UserMeta | null, createdAt?: Date): boolean {
  // Test accounts are NEVER existing - always fresh onboarding
  if (meta?.isTestAccount) return false;
  
  // Use provided createdAt or fall back to stored meta
  const accountCreatedAt = createdAt || (meta?.createdAt ? new Date(meta.createdAt) : null);
  
  // If we have a creation date, check against launch date
  if (accountCreatedAt) {
    return accountCreatedAt < ONBOARDING_LAUNCH_DATE;
  }
  
  // Unknown creation date - assume NEW user (safer default for onboarding)
  return false;
}

function createDefaultProgress(userId: string, isExistingUser: boolean = false): OnboardingProgress {
  // Existing users get all milestones completed automatically
  const completedMilestones: MilestoneId[] = isExistingUser 
    ? MILESTONES.map(m => m.id)
    : [];
  
  return {
    userId,
    completedMilestones,
    currentStep: isExistingUser ? MILESTONES.length : 0,
    hasSeenWelcome: isExistingUser, // Existing users skip welcome
    hasSeenChatTour: isExistingUser,
    hasSeenLibraryTour: isExistingUser,
    hasSeenToolsTour: isExistingUser,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

export const onboardingService = {
  getProgress(userId: string, userCreatedAt?: Date, userEmail?: string): OnboardingProgress {
    // Get or update user meta (persisted separately from progress)
    let meta = getUserMeta(userId);
    if (userCreatedAt || userEmail) {
      // Update meta with fresh data if provided
      meta = saveUserMeta(userId, userEmail, userCreatedAt);
    }
    
    const stored = localStorage.getItem(getStorageKey(userId));
    if (stored) {
      const parsed = JSON.parse(stored);
      const progress = {
        ...parsed,
        createdAt: new Date(parsed.createdAt),
        updatedAt: new Date(parsed.updatedAt),
      };
      
      // Migration: If existing user (created before launch) has empty milestones,
      // auto-unlock all features for them (they shouldn't see onboarding)
      // Uses cached meta for consistent user type determination
      const isExistingUser = isExistingUserByMeta(meta, userCreatedAt);
      if (isExistingUser && progress.completedMilestones.length === 0) {
        console.log('[Onboarding] Migrating existing user to auto-unlocked state');
        const migratedProgress = createDefaultProgress(userId, true);
        this.saveProgress(migratedProgress);
        return migratedProgress;
      }
      
      return progress;
    }
    // No stored progress - determine if this is an existing user using cached meta
    const isExistingUser = isExistingUserByMeta(meta, userCreatedAt);
    const progress = createDefaultProgress(userId, isExistingUser);
    // Save immediately so we don't recalculate next time
    this.saveProgress(progress);
    return progress;
  },

  saveProgress(progress: OnboardingProgress): void {
    localStorage.setItem(getStorageKey(progress.userId), JSON.stringify(progress));
  },

  resetProgress(userId: string): void {
    localStorage.removeItem(getStorageKey(userId));
  },

  resetUserMeta(userId: string): void {
    localStorage.removeItem(getUserMetaKey(userId));
  },

  isTestAccount(email: string | undefined): boolean {
    return email?.toLowerCase() === TEST_EMAIL;
  },

  handleTestAccountLogin(userId: string, email: string | undefined): void {
    if (this.isTestAccount(email)) {
      // Clear both progress AND user meta for complete reset
      this.resetProgress(userId);
      this.resetUserMeta(userId);
      console.log('[Onboarding] Test account detected - resetting progress for groundhog day mode');
    }
  },

  completeMilestone(userId: string, milestoneId: MilestoneId): { 
    isNew: boolean; 
    milestone: Milestone | undefined;
    progress: OnboardingProgress;
  } {
    const progress = this.getProgress(userId);
    const milestone = MILESTONES.find(m => m.id === milestoneId);
    
    if (progress.completedMilestones.includes(milestoneId)) {
      return { isNew: false, milestone, progress };
    }

    progress.completedMilestones.push(milestoneId);
    progress.currentStep = progress.completedMilestones.length;
    progress.updatedAt = new Date();
    this.saveProgress(progress);

    return { isNew: true, milestone, progress };
  },

  hasMilestone(userId: string, milestoneId: MilestoneId): boolean {
    const progress = this.getProgress(userId);
    return progress.completedMilestones.includes(milestoneId);
  },

  getUnlockedFeatures(userId: string): FeatureUnlock[] {
    const progress = this.getProgress(userId);
    const featureMap: Record<string, MilestoneId> = {
      dashboard: 'first_chat',
      library: 'first_space',
      tools: 'explore_library',
      advanced: 'try_ai_tool',
    };

    return Object.entries(featureMap).map(([feature, requiredMilestone]) => ({
      feature,
      isUnlocked: progress.completedMilestones.includes(requiredMilestone),
      requiredMilestone,
      unlockedAt: progress.completedMilestones.includes(requiredMilestone) 
        ? progress.updatedAt 
        : undefined,
    }));
  },

  isFeatureUnlocked(userId: string, feature: 'dashboard' | 'library' | 'tools' | 'advanced'): boolean {
    const unlocks = this.getUnlockedFeatures(userId);
    const featureUnlock = unlocks.find(u => u.feature === feature);
    return featureUnlock?.isUnlocked ?? false;
  },

  isNewUser(userId: string): boolean {
    const progress = this.getProgress(userId);
    return progress.completedMilestones.length === 0;
  },

  markWelcomeSeen(userId: string): void {
    const progress = this.getProgress(userId);
    progress.hasSeenWelcome = true;
    progress.updatedAt = new Date();
    this.saveProgress(progress);
  },

  markTourSeen(userId: string, tour: 'chat' | 'library' | 'tools'): void {
    const progress = this.getProgress(userId);
    if (tour === 'chat') progress.hasSeenChatTour = true;
    if (tour === 'library') progress.hasSeenLibraryTour = true;
    if (tour === 'tools') progress.hasSeenToolsTour = true;
    progress.updatedAt = new Date();
    this.saveProgress(progress);
  },

  getNextMilestone(userId: string): Milestone | undefined {
    const progress = this.getProgress(userId);
    return MILESTONES.find(m => !progress.completedMilestones.includes(m.id));
  },

  getCompletionPercentage(userId: string): number {
    const progress = this.getProgress(userId);
    return Math.round((progress.completedMilestones.length / MILESTONES.length) * 100);
  },

  getTotalXpEarned(userId: string): number {
    const progress = this.getProgress(userId);
    return MILESTONES
      .filter(m => progress.completedMilestones.includes(m.id))
      .reduce((sum, m) => sum + m.xpReward, 0);
  },
};
